/* eslint-disable @typescript-eslint/ban-ts-comment */
import TWEEN from "@tweenjs/tween.js";
import { Throwable } from "@wss/error-tracking/throwable";
import { useMemo, useCallback, useEffect } from "react";
import {
  Scene,
  PerspectiveCamera,
  WebGLRenderer,
  LoadingManager,
  Vector3,
} from "three";
import { OrbitControls } from "three/examples/jsm/controls/OrbitControls";

const constants = {
  yBoundaries: { min: 300, max: 700 },
  xBoundaries: { min: 190, max: 900 },
  tweenTime: 400,
  eventListener: undefined,
  orbitMinDistance: -100,
  orbitMaxDistance: 100,
  cameraPosition: { x: 0, y: 0, z: 40 },
  cameraStartingPosition: { x: 0, y: 0, z: 40 },
  objectRotation: { x: 0, y: 0, z: 0 },
  enableStatsDisplay: false,
  defaultCameraZPosition: 40,
};

export default () => {
  const scene = useMemo(() => new Scene(), []);
  const camera = useMemo(() => new PerspectiveCamera(10, 1, 0.1, 1000), []);
  const renderer = useMemo(
    () => new WebGLRenderer({ antialias: true, alpha: true }),
    [],
  );

  const controls = useMemo(
    () => new OrbitControls(camera, renderer.domElement),
    [camera, renderer],
  );

  const manager = useMemo(() => new LoadingManager(), []);

  /* Renderer */
  const render = useCallback(() => {
    renderer.render(scene, camera);
  }, [renderer, scene, camera]);

  // Loop, runs often
  const animate = useCallback(() => {
    window.requestAnimationFrame(animate);
    TWEEN.update();
    renderer.render(scene, camera);
    // controls.update(); TODO: Do we need this?
  }, [camera, renderer, scene]);

  /* CAMERA ANIMATION */
  // The comments in this section are from the old code...
  const radiansToDegreesRounded = (radian: number) => {
    return Math.round(radian / (Math.PI / 180));
  };

  // convert radians to degrees. We also want to ensure we are dealing with upto a single revolution.
  // I.e. we want 720 degrees converted to 2 PI
  const degreesToRadiansModulated = (degrees: number) => {
    return (degrees * (Math.PI / 180)) % (2 * Math.PI);
  };

  const calculateShortestPathToDestinationAngle = useCallback(
    (destinationAngleDegrees: number, startAngleDegrees: number) => {
      if (Math.abs((destinationAngleDegrees - startAngleDegrees) % 360) < 0.001)
        return degreesToRadiansModulated(startAngleDegrees);
      // Get a number between 0 and 360 (under assumption we are: -360 <= x <= 360)
      if (startAngleDegrees < 0) {
        startAngleDegrees += 360;
      }
      if (destinationAngleDegrees < 0) {
        destinationAngleDegrees += 360;
      }
      let itemToReturn = 0;
      if (destinationAngleDegrees - startAngleDegrees > 180) {
        itemToReturn = destinationAngleDegrees - 360;
      } else {
        itemToReturn = destinationAngleDegrees;
      }
      return degreesToRadiansModulated(itemToReturn);
    },
    [],
  );

  /*
   * Provide rotation, an object containing x, y, and z properties, to this method
   * and it will rotate the model in the scene using the shortest path
   * clockwise/anti-clockwise. By default, it will use TWEENing.
   *
   */
  const rotateModel = useCallback(
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    (rotation: any, animate: any) => {
      const model = scene.getObjectByName("mainProduct");

      return new Promise<Throwable>((accept) => {
        if (!model) {
          accept({
            isError: true,
            error: new Error(
              "Unable to find element with name 'mainProduct' in scene",
            ),
          });
          return;
        }

        const modelRotationAsDegrees = ["x", "y", "z"].reduce(
          (initialValue, key) => {
            // @ts-ignore

            initialValue[key] = radiansToDegreesRounded(model.rotation[key]);
            return initialValue;
          },
          {},
        );

        const rotateTo = Object.keys(rotation).reduce(
          (rotateTo, key) =>
            Object.defineProperty(rotateTo, key, {
              value: calculateShortestPathToDestinationAngle(
                rotation[key],
                // @ts-ignore
                modelRotationAsDegrees[key],
              ),
            }),
          {},
        );

        // if we are going clockwise, we will want to use negative degrees i.e. -90 is the same as 270
        // so, if you are at 20 and need to get to 270, your destination would become -90;
        const timeToUse = animate ? constants.tweenTime : 0;

        new TWEEN.Tween(model.rotation)
          // @ts-ignore
          .to({ y: rotateTo.y, x: rotateTo.x, z: rotateTo.z }, timeToUse)
          .easing(TWEEN.Easing.Quadratic.In)
          .onComplete(() => {
            accept({ isError: false });
          })
          .start();
      });
    },
    [calculateShortestPathToDestinationAngle, scene],
  );

  const repositionCamera = useCallback(
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    (position: any, animate: any) => {
      return new Promise<void>((accept) => {
        const fromValues = {
          controls: {
            x: controls.target.x,
            y: controls.target.y,
            z: controls.target.z,
          },
          camera: {
            x: camera.position.x,
            y: camera.position.y,
            z: camera.position.z,
          },
        };

        const toValues = {
          controls: {
            x: 0,
            y: 0,
            z: 0,
          },
          camera: {
            x: position.x,
            y: position.y,
            z: position.z,
          },
        };

        const timeToUse = animate ? constants.tweenTime : 0;

        // Chain our animations here so we can reset the controls
        new TWEEN.Tween(fromValues.camera)
          .to(toValues.camera, timeToUse)
          .onStart(() => {
            new TWEEN.Tween(fromValues.controls)
              .to(toValues.controls, timeToUse)
              .easing(TWEEN.Easing.Quadratic.InOut)
              .onUpdate((coords) => {
                controls.target = new Vector3(coords.x, coords.y, coords.z);
                controls.update();
              })
              .start();
          })

          .easing(TWEEN.Easing.Quadratic.In)
          .onUpdate((coords) => {
            camera.position.set(coords.x, coords.y, coords.z);
          })
          .onComplete(() => {
            accept();
          })
          .start();
      });
    },
    [camera, controls],
  );

  const transitionObjectView = useCallback(
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    (position: any, rotation: any, animate: any, callback: any) => {
      const itemsToTransition = 2;
      let itemsTransitioned = 0;

      repositionCamera(position, animate).then(() => {
        itemsTransitioned += 1;
        if (itemsTransitioned === itemsToTransition) {
          if (callback) {
            callback();
          }
        }
      });

      rotateModel(rotation, animate).then((result) => {
        if (result.isError) return;
        itemsTransitioned += 1;
        if (itemsTransitioned === itemsToTransition) {
          if (callback) {
            callback();
          }
        }
      });
    },
    [repositionCamera, rotateModel],
  );

  useEffect(
    () => () => {
      controls.dispose();
      renderer.dispose();
    },
    [controls, renderer],
  );

  return {
    scene,
    camera,
    manager,
    controls,
    renderer,
    animate,
    render,
    constants,
    transitionObjectView,
  };
};
